import React from 'react';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { formatConnectionTime } from '../../utils/connectionUtils';

const PrintDispatchButton = ({ runs, tdrData, brockData, disabled }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Please allow popups for this website to use the print feature.");
      return;
    }

    const inboundInfo = tdrData?.inbound || {};
    const drivers = runs?.drivers || [];
    const activeDrivers = drivers.filter(d => d.flights.length > 0);
    
    const binNames = brockData?.bins ? Object.keys(brockData.bins).sort().reverse() : [];

    const displayFlightNumber = (flightNum) => {
      if (!flightNum) return '';
      return flightNum.replace(/^WN\s*/, '');
    };

    const buildDriverSections = () => {
      let genericCounter = 1;
      let hotCounter = 1;
      let priorityCounter = 1;
      let freightCounter = 1;
      let coldCounter = 1;

      return activeDrivers.map(driver => {
        const sortedFlights = [...driver.flights].sort((a, b) => (a.etd || '').localeCompare(b.etd || ''));
        const bagFlights = sortedFlights.filter(f => (f.bags || 0) > 0);
        const freightFlights = sortedFlights.filter(f => f.freightByBin && Object.values(f.freightByBin).some(count => count > 0));
        
        let driverDisplayName;
        const lowerCaseName = driver.name.toLowerCase();

        if (lowerCaseName.includes('hot')) {
          driverDisplayName = `Hot Bag Driver ${hotCounter++}`;
        } else if (lowerCaseName.includes('priority')) {
          driverDisplayName = `Priority Driver ${priorityCounter++}`;
        } else if (lowerCaseName.includes('freight')) {
          driverDisplayName = `Freight Driver ${freightCounter++}`;
        } else if (lowerCaseName.includes('cold')) {
          driverDisplayName = `Cold Bag Driver ${coldCounter++}`;
        } else {
          driverDisplayName = `Driver ${genericCounter++}`;
        }

        const buildFreightTable = () => {
          if (freightFlights.length === 0) return '';
          return `
            <h4 class="freight-header">Freight</h4>
            <table>
              <thead>
                <tr>
                  <th>Flight #</th>
                  <th>Destination</th>
                  <th>Gate</th>
                  <th>ETD</th>
                  <th>Connection</th>
                  <th>Total Freight</th>
                  ${binNames.map(bin => `<th>Bin ${bin}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${freightFlights.map(flight => {
                    const totalFreight = Object.values(flight.freightByBin || {}).reduce((a, b) => a + b, 0);
                    
                    // Format freight display as "TYPE pieces/weight"
                    let freightDisplay = totalFreight.toString();
                    if (flight.allFreight && flight.allFreight.length === 1) {
                      const freight = flight.allFreight[0];
                      const pieces = freight.pieces || freight.count || 0;
                      const weight = freight.weight || 0;
                      freightDisplay = `${freight.type} ${pieces}/${weight}`;
                    } else if (flight.allFreight && flight.allFreight.length > 1) {
                      freightDisplay = `${totalFreight} Freight`;
                    }
                    
                    return `
                      <tr class="${flight.connectionCategory}-row">
                        <td>${displayFlightNumber(flight.flightNumber)}</td>
                        <td>${flight.destination}</td>
                        <td>${flight.gate}</td>
                        <td>${flight.etd}</td>
                        <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                          ${formatConnectionTime(flight.connectionTime)}
                          ${flight.isOvernight ? ' (Next Day)' : ''}
                        </td>
                        <td>${freightDisplay}</td>
                        ${binNames.map(bin => `<td>${flight.freightByBin?.[bin] || 0}</td>`).join('')}
                      </tr>
                    `
                }).join('')}
              </tbody>
            </table>
          `;
        };
        
        return `
          <div class="driver-section">
            <div class="driver-header">
              <h2>${driverDisplayName}</h2>
            </div>
            ${bagFlights.length > 0 ? `
            <h4 class="bag-header">Bag Runs</h4>
            <table>
              <thead>
                <tr>
                  <th>Flight #</th>
                  <th>Destination</th>
                  <th>Gate</th>
                  <th>ETD</th>
                  <th>Connection</th>
                  <th>Total Bags</th>
                  ${binNames.map(bin => `<th>Bin ${bin}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${bagFlights.map(flight => `
                  <tr class="${flight.connectionCategory}-row">
                    <td>${displayFlightNumber(flight.flightNumber)}</td>
                    <td>${flight.destination}</td>
                    <td>${flight.gate}</td>
                    <td>${flight.etd}</td>
                    <td class="${flight.connectionTime < 0 ? 'negative-connection' : ''}">
                      ${formatConnectionTime(flight.connectionTime)}
                      ${flight.isOvernight ? ' (Next Day)' : ''}
                    </td>
                    <td>${flight.bags || 0}</td>
                    ${binNames.map(bin => `<td>${flight.binDistribution?.[bin] || 0}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
            ` : ''}
            
            ${buildFreightTable()}
          </div>
        `;
      }).join('');
    };

    const printDateTime = new Date().toLocaleString();

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Dispatch for Flight ${displayFlightNumber(inboundInfo.flightNumber)}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
            @page { margin: 0.75in; }
            body { font-family: 'Roboto', Arial, sans-serif; color: black; background: white; margin: 0; }
            h1, h2, h3 { margin: 0; padding: 0; text-align: center; }
            .page-header { text-align: center; margin-bottom: 20px; }
            .page-header h1 { font-size: 24px; margin-bottom: 5px; }
            .page-header h3 { font-size: 16px; font-weight: normal; margin-bottom: 15px; }
            .inbound-table { width: 80%; margin: 0 auto 20px auto; border-collapse: collapse; }
            
            .driver-section { 
              /* --- CHANGE: Removed 'page-break-inside: avoid' to allow sections to split across pages --- */
              margin-bottom: 40px;
              border: 1px solid #ccc;
              border-radius: 8px;
              padding: 15px;
              overflow: hidden;
            }
            .driver-header { 
              background-color: #f2f2f2; 
              padding: 10px; 
              margin: -15px -15px 15px -15px;
              border-bottom: 1px solid #ccc;
            }
            .driver-header h2 { font-size: 18px; text-align: left; padding-left: 15px; }
            
            .bag-header, .freight-header { text-align: left; font-size: 14px; margin: 10px 0 5px 0; }
            table { width: 100%; border-collapse: collapse; }
            thead { display: table-header-group; }
            
            /* --- CHANGE: Added rule to ensure tables break cleanly BETWEEN rows --- */
            tr { page-break-inside: avoid; }

            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 12px; }
            th { background-color: #e9ecef; font-weight: bold; }
            tbody tr:nth-child(odd) { background-color: #f9f9f9; }
            
            /* --- CHANGE: Removed the rule that was forcing the Destination column to be left-aligned --- */
            /* td:nth-child(2) { text-align: left; } */

            .priority-row { background-color: rgba(249, 182, 18, 0.2) !important; }
            .hot-row { background-color: rgba(255, 0, 0, 0.1) !important; }
            .cold-row { background-color: rgba(0, 0, 255, 0.1) !important; }
            .negative-connection { color: #D8000C; font-weight: bold; }
            .page-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 12px; color: #666; padding: 10px; }
          </style>
        </head>
        <body>
          <div class="page-header">
            <h1>Inbound Flight: ${displayFlightNumber(inboundInfo.flightNumber)} from ${inboundInfo.arrivingFrom}</h1>
            <h3>Inbound Flight Details</h3>
            <table class="inbound-table">
              <thead>
                <tr>
                  <th>Flight Number</th>
                  <th>Arriving From</th>
                  <th>Registry</th>
                  <th>Arrival Gate</th>
                  <th>ETA</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${displayFlightNumber(inboundInfo.flightNumber)}</td>
                  <td>${inboundInfo.arrivingFrom}</td>
                  <td>${inboundInfo.registry}</td>
                  <td>${inboundInfo.arrivalGate}</td>
                  <td>${inboundInfo.eta}</td>
                </tr>
              </tbody>
            </table>
            <h3>Outbound Flights</h3>
          </div>
          ${buildDriverSections()}
          <div class="page-footer">
            Printed on: ${printDateTime}
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => window.print(), 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <Button
      variant="contained"
      startIcon={<PrintIcon />}
      onClick={handlePrint}
      disabled={disabled}
      sx={{
        backgroundColor: '#6c757d',
        color: 'white',
        '&:hover': {
          backgroundColor: '#5a6268',
        },
        '&.Mui-disabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      Print Runs
    </Button>
  );
};

export default PrintDispatchButton;